/* eslint-disable camelcase */
import queryString from 'query-string';
import React, { useContext, useState, useEffect } from 'react';
import { browserHistory } from 'react-router';

import BSONObjectIdGenerator from 'app/adapters/BSONObjectIdGenerator';
import ReportGenerator from 'app/adapters/ReportGenerator';
import electron from 'app/native/node/electron';
import ViewerWithAutoImportedStudies from 'app/components/Viewer/ViewerWithAutoImportedStudies';
import OnlineInferenceExecutor from 'app/adapters/OnlineInferenceExecutor';
import AppHelmet from 'app/components/AppHelmet';
import CloudBasedPatientStore from 'app/adapters/CloudBasedPatientStore';
import _ from 'lodash';
import { ImagePath } from 'app/interfaces/ImageLoader';
import { ViewerMode, isViewerMode } from 'app/components/Viewer/types';
import HideHeaderFnProvider from 'app/providers/HideHeaderFnProvider/context';
import IImageProcessorContext from 'app/providers/ImageProcessorProvider/context';
import StudyStoreContext from 'app/providers/StudyStoreProvider/context';
import ImageLoaderContext from 'app/providers/ImageLoader/context';

type ParsedQuery = {
  [key: string]: string | string[];
};

const parseInitialStudyId = (queryStringParsed: ParsedQuery) => {
  if (!queryStringParsed.study_ids) return undefined;

  return queryStringParsed.study_ids as string[];
};
const parseInitialStudyInstanceUids = (queryStringParsed: ParsedQuery) => {
  if (!queryStringParsed.studyInstanceUIDs) return undefined;

  return queryStringParsed.studyInstanceUIDs as string[];
};

const parseImagesInQuery = (queryStringParsed: ParsedQuery) => {
  if (!queryStringParsed.images) return undefined;

  return (queryStringParsed.images as string[]).map((path: string) => ({ path }));
};

const parseQueryMode = ({ mode }: ParsedQuery = {}): ViewerMode => {
  if (!mode) return undefined;
  return isViewerMode(mode) ? mode : undefined;
};

const parseInitialPatientInfoInQuery = ({
  animalName,
  ownerName,
  patient_id,
}: ParsedQuery = {}) => {
  if (animalName || ownerName || patient_id)
    return {
      name: animalName as string,
      owner_name: ownerName as string,
      _id: patient_id as string,
    };
  return undefined;
};

type newCommandLineArgumentsPayload = {
  images: string[];
  ownerName: string;
  animalName: string;
};

function ProductionViewer() {
  const queryStringParsed = queryString.parse(window.location.search, { arrayFormat: 'bracket' });

  const hideHeader = useContext(HideHeaderFnProvider);
  const imageProcessor = useContext(IImageProcessorContext);
  const [initialStudyIds] = useState(() => parseInitialStudyId(queryStringParsed));
  const [initialStudyInstanceUids] = useState(() =>
    parseInitialStudyInstanceUids(queryStringParsed)
  );
  const [imagePaths, setImagePaths] = useState(() => parseImagesInQuery(queryStringParsed));
  const [initialPatientInfo] = useState(() => parseInitialPatientInfoInQuery(queryStringParsed));
  const [mode] = useState(() => parseQueryMode(queryStringParsed));
  const [objectIdGenerator] = useState(() => new BSONObjectIdGenerator());
  const imageLoader = useContext(ImageLoaderContext);
  const [cloudBasedPatientStore] = useState(() => new CloudBasedPatientStore());
  const studyStore = useContext(StudyStoreContext);
  const [inferenceExecutor] = useState(() => new OnlineInferenceExecutor());
  const [reportGenerator] = useState(() => new ReportGenerator());

  const acknowledgeImagePaths = (acknowledgedImagePaths: ImagePath[]) => {
    setImagePaths(_.difference(imagePaths, acknowledgedImagePaths));
  };

  const setupCommandLineListener = () => {
    if (!electron()) return undefined;

    const { ipcRenderer } = electron();

    const refreshPageOnCommandLineChange = (
      _event: any,
      { images, ownerName, animalName }: newCommandLineArgumentsPayload
    ) => {
      if (!images && !ownerName && !animalName) return;
      setImagePaths(images.map((path) => ({ path })));
    };

    ipcRenderer.on('newCommandLineArguments', refreshPageOnCommandLineChange);

    return () => {
      ipcRenderer.removeListener('newCommandLineArguments', refreshPageOnCommandLineChange);
    };
  };

  useEffect(setupCommandLineListener, []);

  return (
    <>
      <AppHelmet titleIntlID="demo.pageTitle" descriptionIntlID="demo.pageDescription" />
      <ViewerWithAutoImportedStudies
        imageLoader={imageLoader}
        objectIdGenerator={objectIdGenerator}
        studyStore={studyStore}
        patientStore={cloudBasedPatientStore}
        inferenceExecutor={inferenceExecutor}
        initialStudyIds={initialStudyIds}
        initialStudyInstanceUids={initialStudyInstanceUids}
        imageProcessor={imageProcessor}
        reportGenerator={reportGenerator}
        hideHeader={hideHeader}
        history={browserHistory}
        imagePaths={imagePaths}
        initialPatientInfo={initialPatientInfo}
        onImagePathsAcknowledged={acknowledgeImagePaths}
        mode={mode}
      />
    </>
  );
}

export default ProductionViewer;
